import { Stack } from '@mui/material';
import { useLocalStorage } from 'react-use';

import { Accordion } from '@/components/Accordion/Accordion';
import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  MiniTable,
  MiniTableRow,
} from '@/components/tables/MiniTable/MiniTable';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { UseFormReturn } from '@/types/react-hook-form';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import { MemoDetailsFormShape } from '../MemoDetails.types';

interface MemoAboutSectionProps {
  formMethods: UseFormReturn<MemoDetailsFormShape>;
  onSubmit: () => void;
  isDirty: boolean;
  savingMemoName: boolean;
  canEditHouseholdData: boolean;
  aboutRows: MiniTableRow[];
}

export function MemoAboutSection({
  formMethods,
  onSubmit,
  isDirty,
  savingMemoName,
  canEditHouseholdData,
  aboutRows,
}: MemoAboutSectionProps) {
  const [accordionExpanded, setAccordionExpanded] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.MEMO_DETAILS_ABOUT_ACCORDION_EXPANDED,
    true,
    localStorageBooleanSerializer
  );

  return (
    <Accordion
      variant="default"
      title="About this memo"
      isDefaultExpanded={accordionExpanded}
      onChange={setAccordionExpanded}
      withDivider={false}
    >
      <HouseholdPermissionAwareDisabledProvider>
        <Stack
          component="form"
          spacing={2.5}
          onSubmit={canEditHouseholdData ? onSubmit : undefined}
        >
          <FormAwareTextInput<MemoDetailsFormShape>
            control={formMethods.control}
            fieldName="memoName"
            label="Memo name"
            required
          />

          <MiniTable rows={aboutRows} variant="default" />

          {canEditHouseholdData && (
            <Button
              size="sm"
              variant="primary"
              onClick={onSubmit}
              loading={savingMemoName}
              disabled={!isDirty}
              sx={{ alignSelf: 'flex-start' }}
            >
              Save changes
            </Button>
          )}
        </Stack>
      </HouseholdPermissionAwareDisabledProvider>
    </Accordion>
  );
}
